// Medications.tsx
import React, { useState, useEffect, useRef } from "react";
import { useResusContext } from "../Resus/ResusContext";
import { useCPRLog } from "./CPRLog";
import { useCPRState } from "./CprState/CPRStateContext";
import { loadCurrentState, saveCurrentState } from "./CprState/storage";
import { cprEventEmitter, EVENTS } from "./cprEvents";
import MedicationTable from "./MedicationTable";
import MedicationSearch from "./MedicationSearch";
import { Drug, Drip, AdministrationDetails, MedicationItem } from "./types";
import { MedicationGivenInfo } from "./CprState/types";
import { useTranslation } from 'react-i18next';

import "./Medications.scss";
import { getHospitalData } from "./PerHospitalResusConfig";

const Medications: React.FC = () => {
  const searchInputRef = useRef<HTMLButtonElement>(null);
  const { weight, protocols, hospital } = useResusContext();
  const [inCprProcedure, setInCprProcedure] = useState(false);
  const { dispatch, state } = useCPRState();
  const { addEntry } = useCPRLog();
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const { t } = useTranslation(['resus']);
  
  const hospitalData = getHospitalData(hospital);
  const drugsData = hospitalData.drugsData;
  const protocolsData = hospitalData.protocols;

  // Load selected drugs from local storage
  const [selectedDrugs, setSelectedDrugs] = useState<string[]>(() => {
    const savedState = loadCurrentState();
    return savedState?.medications?.selectedDrugs || [];
  });

  // Load displayed sections from local storage
  const [displayedSections, setDisplayedSections] = useState<string[]>(() => {
    const savedState = loadCurrentState();
    return savedState?.medications?.displayedSections || [];
  });

  // Load last given info from local storage
  const [lastGivenInfo, setLastGivenInfo] = useState<{ [key: string]: MedicationGivenInfo }>(() => {
    const savedState = loadCurrentState();
    return savedState?.medications?.lastGivenInfo || {};
  });

  useEffect(() => {
    const unsubscribe = cprEventEmitter.subscribe(EVENTS.ADD_MEDS_SECTION, (sectionName?: string) => {
      if (sectionName) {
        addSection(sectionName);
      }
    });
    return () => unsubscribe();
  }, [displayedSections]);

  const handleClearSelectedDrugs = () => {
    setSelectedDrugs([]);
    saveCurrentState(
      {
        medications: {
          ...loadCurrentState()?.medications,
          selectedDrugs: [],
        },
      },
      "medications"
    );
  };

  const handleRemoveSection = (sectionName: string) => {
    const newSections = displayedSections.filter(s => s !== sectionName);
    setDisplayedSections(newSections);
    saveCurrentState(
      {
        medications: {
          ...loadCurrentState()?.medications,
          displayedSections: newSections,
        },
      },
      "medications"
    );
  };

  // Handle click outside to close the modal
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const modalElement = document.querySelector('.modal');
      const searchBoxElement = searchInputRef.current;

      if (isSearchOpen && modalElement &&
        !modalElement.contains(event.target as Node) &&
        searchBoxElement && !searchBoxElement.contains(event.target as Node)) {
        handleSearchClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isSearchOpen]);

  // Effect for CPR reset
  useEffect(() => {
    const unsubscribe = cprEventEmitter.subscribe(EVENTS.RESET_CPR, () => {
      setLastGivenInfo({});
      setSelectedDrugs([]);
      setDisplayedSections([]);
      setIsSearchOpen(false);
      setInCprProcedure(false);
    });
    return () => unsubscribe();
  }, []);

  // Effect for protocol-based section changes
  useEffect(() => {
    if (!state.isRunning && !state.endTime) {
      setInCprProcedure(false);
      return;
    }
    const hasSymptomaticBradycardiaOrCardiacArrest = protocols.some(
      p => p === "symptomatic-bradycardia" || p === "cardiac-arrest"
    );
    if (hasSymptomaticBradycardiaOrCardiacArrest) {
      setInCprProcedure(false); // the resus drugs already displayed as part of the protocol.      
    }
    else { // no cpr protocol, and cpr is active    
      setInCprProcedure(true);
    }
  }, [protocols, state]);

  const handleSearchFocus = () => {
    setIsSearchOpen(true);
    if (searchInputRef.current) {
      searchInputRef.current.blur();
    }
  };

  const handleSearchClose = () => {
    setIsSearchOpen(false);
    if (searchInputRef.current) {
      searchInputRef.current.blur();
    }
  };

  // Handle adding a new section
  const addSection = (sectionName: string) => {
    if (!allSections.find((section) => section.name === sectionName)) {
      return; // no need to add non existing section.
    }
    if (!displayedSections.includes(sectionName)) {
      const newSections = [...displayedSections, sectionName];
      setDisplayedSections(newSections);
      saveCurrentState(
        {
          medications: {
            ...loadCurrentState()?.medications,
            displayedSections: newSections,
          },
        },
        "medications"
      );
    }
  };

  // Selection handler
  const handleSelectionChange = (newSelectedDrugs: string[]) => {
    setSelectedDrugs(newSelectedDrugs);
    saveCurrentState(
      {
        medications: {
          ...loadCurrentState()?.medications,
          selectedDrugs: newSelectedDrugs,
        },
      },
      "medications"
    );
  };

  // Process all sections
  const allSections = drugsData.sections.map((section) => ({
    name: section.name,
    drugs: section.drugs
      .map((drugId) => {
        const drug = [
          ...drugsData.drugs.map((drug) => ({ ...drug, isDrip: false } as Drug)),
          ...drugsData.drips.map((drip) => ({ ...drip, isDrip: true } as Drip))
        ].find((drug) => drug.id === drugId);
        return drug;
      })
      .filter((drug) => drug !== undefined),
  }));

  // Get protocol name
  const getProtocolName = (protocolId: string): string => {
    const protocolData = protocolsData.emergencyProtocols
      .flatMap((section) => section.protocols)
      .find((p) => p.id === protocolId);
    return protocolData?.name || "";
  };  

  // Handle giving medication
  const handleGiveMedication = (item: MedicationItem, administrationDetails: AdministrationDetails) => {
    React.startTransition(() => {
      const currentTime = new Date().toLocaleTimeString("he-IL", {
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      });
  
      addEntry({
        timestamp: new Date().toISOString(),
        text: administrationDetails.resusLog,
        type: "medication",
        isImportant: false,
      });
  
      const newLastGivenInfo = {
        ...lastGivenInfo,
        [item.id]: {
          time: currentTime,
          details: administrationDetails,
        },
      };
  
      setLastGivenInfo(newLastGivenInfo);
      saveCurrentState(
        {
          medications: {
            ...loadCurrentState()?.medications,
            lastGivenInfo: newLastGivenInfo,
          },
        },
        "medications"
      );
  
      if (item.name.toLowerCase().includes("adrenaline")) {
        dispatch({ type: "INCREMENT_ADRENALINE" });
        cprEventEmitter.emit(EVENTS.ADRENALINE_GIVEN);
      }
    });
  };

  // Get protocol medications
  const getProtocolMedications = (protocolId: string) => {
    const selectedProtocol = drugsData.protocols.find((p) => p.protocolId === protocolId);
    if (!selectedProtocol) return { items: [], protocol: null };

    const protocolDrugs =
      selectedProtocol.drugs
        ?.map((drugId) => {
          const drug = drugsData.drugs.find((drug) => drug.id === drugId);
          return drug ? { ...drug, isDrip: false as const } : undefined;
        })
        .filter((drug) => drug !== undefined) || [];

    const protocolDrips =
      selectedProtocol.drips
        ?.map((dripId) => {
          const drip = drugsData.drips.find((drip) => drip.id === dripId);
          return drip ? { ...drip, isDrip: true as const } : undefined;
        })
        .filter((drip) => drip !== undefined) || [];

    return {
      items: [...protocolDrugs, ...protocolDrips],
      protocol: selectedProtocol
    };
  };

  // Get all protocol medications
  const protocolMedications = protocols
    .map(protocolId => ({
      ...getProtocolMedications(protocolId),
      protocolId
    }))
    .filter(({ items }) => items.length > 0);

  // Get selected medications
  const selectedMedicationItems = selectedDrugs
    .map(drugId => {
      const drug = drugsData.drugs.find(d => d.id === drugId);
      const drip = drugsData.drips.find(d => d.id === drugId);
      return drug ? { ...drug, isDrip: false as const } : drip ? { ...drip, isDrip: true as const } : undefined;
    })
    .filter((item): item is MedicationItem => item !== undefined);

  return (
    <div id="medications" style={{ marginTop: "15px", marginBottom: "15px", textAlign: 'center'}}>
      {!weight && (
        <div
          style={{
            color: "red",
            textAlign: "right",
            marginBottom: "20px",
            direction: "rtl",
            fontWeight: "bold",

          }}
        >
          {t('medications.enterWeightFirst')}
        </div>
      )}

      <button
        ref={searchInputRef}
        onClick={handleSearchFocus}
        className="btn primary-mashlom-btn"
        style={{ marginBottom: "1rem", width: '250px', height: '48px' }}
      >
        <i className="fa fa-plus"></i> {t('medications.addMedications')}
      </button>

      {isSearchOpen && (
        <MedicationSearch
          protocolsData={protocolsData}
          allMedications={[
            ...drugsData.drugs.map(drug => ({ ...drug, isDrip: false as const })),
            ...drugsData.drips.map(drip => ({ ...drip, isDrip: true as const }))
          ]}
          protocols={drugsData.protocols}
          sections={drugsData.sections}
          selectedDrugs={selectedDrugs}
          onClose={handleSearchClose}
          onSelectionChange={handleSelectionChange}
        />
      )}

      {/* Selected Medications */}
      {selectedMedicationItems.length > 0 && (
        <MedicationTable
          title={t('medications.selectedMedications')}
          items={selectedMedicationItems}
          lastGivenInfo={lastGivenInfo}
          onGiveMedication={handleGiveMedication}
          showRemoveButton={true}
          onRemove={handleClearSelectedDrugs}
        />
      )}

      {/* Protocol Medications */}
      {protocolMedications.map(({ items, protocol: protocolData, protocolId }) => (
        <div id={`section-${protocolId}`} key={protocolId}>
          <MedicationTable
            key={protocolId}
            title={`${t('medications.title')} ${getProtocolName(protocolId)}`}
            items={items}
            lastGivenInfo={lastGivenInfo}
            onGiveMedication={handleGiveMedication}
            drugComments={protocolData?.drugComments}
          />
        </div>
      ))}

      {/* Display all requested sections */}
      {displayedSections.map((sectionName) => {
        const sectionDrugs = allSections.find((section) => section.name === sectionName)?.drugs || [];
        if (sectionDrugs.length > 0) {
          return (
            <div id={`section-${sectionName}`} key={sectionName}>
              <MedicationTable
                title={sectionName}
                items={sectionDrugs}
                lastGivenInfo={lastGivenInfo}
                onGiveMedication={handleGiveMedication}
                showRemoveButton={true}
                onRemove={() => handleRemoveSection(sectionName)}
              />
            </div>
          );
        }
        return null;
      })}

      {/* Original CPR drugs section */}
      {inCprProcedure && (
        <MedicationTable
          key={"resus-drugs"}
          title={t('medications.resuscitationDrugs')}
          items={
            allSections.find((section) => section.name === "תרופות החייאה")?.drugs || []
          }
          lastGivenInfo={lastGivenInfo}
          onGiveMedication={handleGiveMedication}
        />
      )}
    </div>
  );
};

export default Medications;